import { US_COUNTRY_ALIASES } from '@sib/config';
import set from 'lodash/set';

export const handleExternalLink = (url: string) =>
  url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`;

export const getInitials = (name?: string | null) => {
  if (!name) return '';
  const nameToSplit = removeCompanyFromName(name);
  const parts = nameToSplit.split(' ');
  const firstPart = parts[0];
  const lastPart = parts[parts.length - 1];

  return parts.length < 2 ? firstPart[0] : `${firstPart[0]}${lastPart[0]}`;
};

export const removeCompanyFromName = (value: string) =>
  value.replace(/\s*\(.*?\)$/, '').trim();

export const capitalizeWords = (value: string) =>
  value.replace(/\b\w/g, char => char.toUpperCase());

export const extractNameFromEmail = (email?: string) => {
  if (!email) return '';
  const name = email.split('@')[0];

  return name
    ? capitalizeWords(removeCompanyFromName(name).replace('.', ' '))
    : '';
};

export const snakeToTitle = (str: string) =>
  str.split('_').map(capitalizeWords).join(' ');

export const firstLetterUppercase = (text: string = '') =>
  text ? text[0].toUpperCase() + text.slice(1) : '';

export const cleanString = (val?: string) =>
  val && val.trim().length > 0 ? val : null;

export const trimValue = (value?: string | null) => value?.trim() ?? '';

export const areStringsEqualIgnoreCase = (a: string, b: string) =>
  a.toLowerCase() === b.toLowerCase();

export const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const toTitleCase = (value: string) =>
  value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(
      word => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    )
    .join(' ');

export const formatTextWithFallback = (
  value: string | null | undefined,
  fallback: string
) => value?.trim() || fallback;

export const numberToOrdinal = (number: number) => {
  const suffix = ['th', 'st', 'nd', 'rd'];
  const v = number % 100;

  return `${number}'${suffix[(v - 20) % 10] || suffix[v] || suffix[0]}`;
};

export const objectFromPath = <T>(path: string, value: T) =>
  set({}, path, value);

export const isExternalHref = (href: string) => /^https?:\/\//i.test(href);

export const formatAddressText = (address?: string) => {
  const raw = address?.trim();
  if (!raw) return null;

  const lines = raw
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  if (lines.length > 1) return lines.join('\n');

  const [line] = lines;
  if (!line) return null;

  const comma = line.indexOf(', ');
  if (comma > 0) {
    const rest = line.slice(comma + 2).trim();
    if (rest) return `${line.slice(0, comma).trim()}\n${rest}`;
  }

  return line;
};

export type StructuredAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
};

const trimAndFilterParts = (parts: (string | null | undefined)[]): string[] =>
  parts
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part));

export const formatStructuredAddress = (
  address?: StructuredAddress | null,
  usCountryAliases: readonly string[] = US_COUNTRY_ALIASES
): string | null => {
  if (!address) return null;

  const cityStateZip = trimAndFilterParts([
    address.city,
    address.state,
    address.zipCode,
  ]).join(' ');

  const streetLine = trimAndFilterParts([address.line1, address.line2]).join(
    ', '
  );

  const country =
    address.country?.trim() &&
    !usCountryAliases.includes(address.country.trim())
      ? address.country.trim()
      : null;

  const formatted = trimAndFilterParts([
    streetLine,
    cityStateZip,
    country,
  ]).join(' ');

  return formatted || null;
};
